export interface FuelSearchRequest {
  column: string;
  operation: string;
  value: string | number | boolean;
  join_table: string;
}

export interface FuelFilterParameter {
  name?: string;
  parameterName?: string;
  expression?: { operator?: string };
}

export interface FuelFilterRequestBody {
  search_requests?: FuelSearchRequest[];
  global_operator?: string;
  page?: {
    page_no?: number;
    page_size?: number;
    sort?: string;
    sort_by_column?: string;
  };
  _offset?: number;
  _page_size?: number;
  _parameters?: FuelFilterParameter[];
  company?: object;
  working_period?: object;
  company_id?: number | string | null;
  working_period_id?: number | string | null;
  [key: string]:
    | FuelSearchRequest[]
    | string
    | number
    | boolean
    | object
    | FuelFilterParameter[]
    | null
    | undefined;
}

const FUEL_FILTER_ENVELOPE_KEYS = new Set([
  'search_requests',
  'global_operator',
  'page',
  '_offset',
  '_page_size',
  '_parameters',
  'company',
  'working_period',
  'company_id',
  'working_period_id',
  /** Legacy kl-grid flag — not a fuel entity column; must not become search_requests. */
  'compress',
]);

const GRID_TO_FUEL_OPERATOR: Record<string, string> = {
  LIKE: 'LIKE',
  NOT_LIKE: 'NOT_LIKE',
  EQ: 'EQUAL',
  NOT_EQ: 'NOT_EQUAL',
  START_WITH: 'BEGINS_WITH',
  END_WITH: 'ENDS_WITH',
  GT: 'GREATER_THAN',
  GE: 'GREATER_THAN_EQUAL',
  LT: 'LESS_THAN',
  LE: 'LESS_THAN_EQUAL',
};

function isScalarFilterValue(value: unknown): value is string | number | boolean {
  const valueType = typeof value;
  return valueType === 'string' || valueType === 'number' || valueType === 'boolean';
}

function resolveFuelOperation(
  column: string,
  parameters: FuelFilterParameter[] | undefined,
): string {
  const param = parameters?.find(
    (entry) => entry.name === column || entry.parameterName === column,
  );
  const gridOperator = param?.expression?.operator;
  if (gridOperator && GRID_TO_FUEL_OPERATOR[gridOperator]) {
    return GRID_TO_FUEL_OPERATOR[gridOperator];
  }
  return 'LIKE';
}

function upsertSearchRequest(
  searchRequests: FuelSearchRequest[],
  entry: FuelSearchRequest,
): FuelSearchRequest[] {
  const index = searchRequests.findIndex((item) => item.column === entry.column);
  if (index === -1) {
    return [...searchRequests, entry];
  }
  const next = [...searchRequests];
  next[index] = entry;
  return next;
}

/** Move kl-grid toolbar filter fields into fuel API search_requests. */
export function transformFuelFilterRequestBody(body: FuelFilterRequestBody): FuelFilterRequestBody {
  if (!Array.isArray(body.search_requests)) {
    return body;
  }

  const parameters = body._parameters;
  let searchRequests = [...body.search_requests];
  let movedToolbarField = false;

  for (const [key, rawValue] of Object.entries(body)) {
    if (FUEL_FILTER_ENVELOPE_KEYS.has(key)) {
      continue;
    }
    if (!isScalarFilterValue(rawValue) || rawValue === '') {
      continue;
    }

    searchRequests = upsertSearchRequest(searchRequests, {
      column: key,
      operation: resolveFuelOperation(key, parameters),
      value: rawValue,
      join_table: '',
    });
    delete body[key];
    movedToolbarField = true;
  }

  body.search_requests = searchRequests;
  if (movedToolbarField) {
    body.global_operator = 'AND';
  }

  if (body._parameters !== undefined) {
    delete body._parameters;
  }

  delete body['compress'];

  return body;
}
